import { useState, useEffect, useCallback } from 'react';
import { dashboardService } from '../services/analytics';
import { DashboardMetrics } from '../types/analytics';
import { getApiErrorMessage } from '../utils/errors';

export const useDashboardMetrics = (providerId: number | null) => {
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!providerId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await dashboardService.getMetrics(providerId);
      setMetrics(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to fetch dashboard metrics'));
      console.error('Error fetching dashboard metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [providerId]);

  const refresh = useCallback(async () => {
    if (!providerId) return;

    try {
      setError(null);
      const data = await dashboardService.refreshMetrics(providerId);
      setMetrics(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to refresh metrics'));
      console.error('Error refreshing metrics:', err);
    }
  }, [providerId]);

  useEffect(() => {
    fetchMetrics();

    // Auto-refresh every 5 minutes
    const interval = setInterval(fetchMetrics, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [fetchMetrics]);

  return { metrics, loading, error, refresh };
};
